import './scss/favorite-button.scss'

import { useEffect, useState } from 'react'

import { iconHeartFilled } from '@/assets/IconHeartFilled'
import { iconHeart } from '@/assets/IconHeart'
import type { Produto } from '../types/produto.props'

interface FavoriteButtonProps {
    readonly produto: Produto
}

export function FavoriteButton({ produto }: FavoriteButtonProps) {
    const [isFavorited, setIsFavorited] = useState(false)
    const [isLoading, setIsLoading] = useState(false)

    const FAVORITOS_UPDATED_EVENT = 'zord:favoritos-updated'

    const notifyFavoritosUpdate = () => {
        globalThis.window.dispatchEvent(new CustomEvent(FAVORITOS_UPDATED_EVENT))
    }

    //caarrega estado do localStorage ao montar o componente
    useEffect(() => {
        const checkFavoriteState = () => {
            try {
                if (typeof Zord !== 'undefined' && Zord.favoritos) {
                    const isFav = Zord.favoritos.existeItemLocalStorage(produto.derivacao_id)
                    setIsFavorited(isFav)
                }
            } catch (error) {
                console.error('[Favoritos] Erro ao carregar estado do favorito:', error)
            }
        }

        checkFavoriteState()

        //listener para atualizar quando localStorage muda em outra aba/janela
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === 'zord_favoritos') {
                checkFavoriteState()
            }
        }

        const handleFavoritosUpdated = () => {
            checkFavoriteState()
        }

        globalThis.window.addEventListener('storage', handleStorageChange)
        globalThis.window.addEventListener(FAVORITOS_UPDATED_EVENT, handleFavoritosUpdated)

        return () => {
            globalThis.window.removeEventListener('storage', handleStorageChange)
            globalThis.window.removeEventListener(FAVORITOS_UPDATED_EVENT, handleFavoritosUpdated)
        }
    }, [produto.derivacao_id])

    const handleFavoriteClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        if (e.stopPropagation) {
            e.stopPropagation()
        }
        if (e.preventDefault) {
            e.preventDefault()
        }

        if (isLoading) return

        try {
            if (typeof Zord === 'undefined' || !Zord.favoritos) {
                showToast('Erro ao atualizar favorito', 'error')
                return
            }

            const jaExiste = Zord.favoritos.existeItemLocalStorage(produto.derivacao_id)

            setIsFavorited(!jaExiste)
            setIsLoading(true)

            if (jaExiste) {
                // Remove favorito (via API se logado, localStorage se não)
                Zord.favoritos.removeFavorito(
                    produto.derivacao_id,
                    () => {
                        setIsLoading(false)
                        notifyFavoritosUpdate()
                        showToast('Removido dos favoritos', 'success')
                    },
                    (message) => {
                        setIsFavorited(true)
                        setIsLoading(false)
                        showToast(message || 'Erro ao remover favorito', 'error')
                    }
                )
            } else {
                // Adiciona favorito (via API se logado, localStorage se não)
                Zord.favoritos.addFavorito(
                    produto.derivacao_id,
                    () => {
                        setIsLoading(false)
                        notifyFavoritosUpdate()
                        showToast('Adicionado aos favoritos', 'success')
                    },
                    (message) => {
                        setIsFavorited(false)
                        setIsLoading(false)
                        showToast(message || 'Erro ao adicionar favorito', 'error')
                    }
                )
            }
        } catch (error) {
            console.error('[Favoritos] Erro ao alternar favorito:', error)
            setIsFavorited((prev) => prev)
            setIsLoading(false)
            showToast('Erro ao atualizar favorito', 'error')
        }
    }

    const showToast = (message: string, type: 'success' | 'error') => {
        if (typeof Zord !== 'undefined' && Zord.msgToast) {
            Zord.msgToast(message, type === 'success' ? 'success' : 'error', 3000, 'top-right')
        }
    }

    return (
        <button
            onClick={handleFavoriteClick}
            className="favorite-button"
            aria-label={isFavorited ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
            title={isFavorited ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
        >
            <span>
                {isFavorited ? iconHeartFilled() : iconHeart()}
            </span>
        </button>
    )
}